/**
 * ServerOrderComplete - Order Confirmation Screen for Server Terminal
 * Shows success message after order is sent to kitchen stations
 */
import { ShoppingBag, Send, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ServerOrderComplete({ 
  orderComplete, 
  onAddMore, 
  onViewTables, 
  onNewOrder 
}) {
  const isTakeaway = orderComplete.orderType === 'takeaway';
  const isFreeCoffeePass = orderComplete.isFreeCoffeePass;
  
  return (
    <div className={`min-h-screen flex items-center justify-center p-8 ${
      isTakeaway 
        ? 'bg-gradient-to-b from-blue-900 to-blue-950' 
        : 'bg-gradient-to-b from-indigo-900 to-indigo-950'
    }`} data-testid="server-order-complete">
      <div className="text-center text-white">
        <div className={`w-32 h-32 mx-auto mb-8 rounded-full flex items-center justify-center animate-bounce ${
          isTakeaway ? 'bg-blue-500' : 'bg-indigo-500'
        }`}>
          {isTakeaway ? <ShoppingBag className="w-16 h-16" /> : <Send className="w-16 h-16" />}
        </div>
        <h1 className="text-5xl font-bold mb-4">
          {isFreeCoffeePass ? 'Sent to Barista!' : 'Sent to Stations!'}
        </h1>
        <div className="bg-white/10 rounded-3xl p-8 inline-block mb-8">
          <div className={`text-lg mb-2 ${isTakeaway ? 'text-blue-200' : 'text-indigo-200'}`}>
            Order Number
          </div>
          <div className="text-8xl font-bold">{orderComplete.order_number}</div>
        </div>
        <div className="space-y-2 mb-8">
          {isTakeaway ? (
            <>
              <div className="inline-flex items-center gap-2 bg-blue-500/30 px-6 py-2 rounded-full">
                <ShoppingBag className="w-6 h-6" />
                <span className="text-2xl font-bold">TAKE AWAY</span>
              </div>
              {isFreeCoffeePass ? (
                <p className="text-xl text-blue-200">Free Coffee Pass - No Payment Needed</p>
              ) : (
                <p className="text-xl text-blue-200">Total: ${(orderComplete.total || 0).toFixed(2)} - Sent to POS for Payment</p>
              )}
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 bg-indigo-500/30 px-6 py-2 rounded-full">
                <UtensilsCrossed className="w-6 h-6" />
                <span className="text-2xl font-bold">TABLE {orderComplete.table}</span>
              </div>
              <p className="text-xl text-indigo-200">Total: ${(orderComplete.total || 0).toFixed(2)} (Payment Pending at POS)</p>
            </>
          )}
        </div>
        
        <div className="flex flex-col gap-4 items-center">
          {!isTakeaway && (
            <div className="flex gap-4">
              <Button 
                onClick={() => onAddMore(orderComplete.table)}
                className="px-8 py-6 text-xl bg-white text-indigo-900 hover:bg-white/90"
                data-testid="server-add-more-btn"
              >
                Add More to Table {orderComplete.table}
              </Button>
              <Button 
                onClick={onViewTables}
                className="px-8 py-6 text-xl bg-green-600 text-white hover:bg-green-700"
                data-testid="server-view-tables-btn"
              >
                View My Tables
              </Button>
            </div>
          )}
          <Button 
            onClick={onNewOrder}
            variant={isTakeaway ? "default" : "outline"}
            className={isTakeaway 
              ? "px-8 py-6 text-xl bg-white text-blue-900 hover:bg-white/90"
              : "px-8 py-6 text-xl border-white/30 text-white hover:bg-white/10"
            }
            data-testid="server-new-table-btn"
          >
            {isTakeaway ? 'New Order' : 'New Table'}
          </Button>
        </div>
      </div>
    </div>
  );
}
